import { createWalletInteractionAnalytics } from "../../utils/walletInteractionAnalytics";
import React, { useState, useEffect, useRef } from "react";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { setApplePayAvailible } from "../../store/reducers/payment";
import { API_URL, EU_STRIPE_PRICE_ID, TRIAL_DAYS, STRIPE_PRICE_ID } from "../../constants/formConstants";
import axios from "axios";
import { getAcquisitionCountryMetadataFromSearch } from "../../utils/acquisitionCountry";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../../utils/checkoutMetadata";
import { pushPurchaseEvent } from "../../utils/gtm";
import { getClientReferenceId, sendAmplitudePurchase } from "../../utils/amplitude";
import { DEFAULT_WALLET_PAYMENT_LABEL, getPaywallContext } from "../../utils/paywallContext";
import {
  getWalletAvailabilityPayload,
  sendPaywallEvent,
} from "../../utils/paywallAnalytics";
import { getTrialOfferId } from "../../utils/paywallPricing";

const AppleGooglePayButton = ({
  trialCost,
  paymentAmount,
  paymentCurrency = "usd",
  paymentValue,
  onSuccess,
  isDisabled = false,
}) => {
  const walletInteractionRef = useRef(new WeakMap());
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestKey, setPaymentRequestKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const stripe = useStripe();
  const dispatch = useDispatch();

  const clientReferenceId = getClientReferenceId();
  const pathname = window.location?.pathname || "";
  const { paywall, paymentLabel } = getPaywallContext(pathname);
  const walletPaymentLabel = paymentLabel || DEFAULT_WALLET_PAYMENT_LABEL;
  const { acquisition_country: acquisitionCountry } = getAcquisitionCountryMetadataFromSearch(window.location.search);
  const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 46 });
  const paymentCurrencyCode = String(paymentCurrency || "usd").toLowerCase();
  const offerId = getTrialOfferId(paymentCurrencyCode);
  const metadataPriceId =
    paymentCurrencyCode === "eur" ? EU_STRIPE_PRICE_ID : STRIPE_PRICE_ID;
  const purchaseValue = Number.isFinite(Number(paymentValue))
    ? Number(paymentValue)
    : Number(trialCost);
  const computedPaymentAmount = Number.isFinite(Number(paymentAmount))
    ? Math.round(Number(paymentAmount))
    : Math.round(Number(trialCost) * 100);
  const persistTrackingContext = () => {
    try {
      if (paywall) {
        sessionStorage.setItem("lastPaywall", paywall);
      }
      if (clientReferenceId) {
        sessionStorage.setItem("clientReferenceId", clientReferenceId);
      }
    } catch (_) {}
  };

  const finishTrialPurchase = async (status = "succeeded") => {
    persistTrackingContext();
    sendPaywallEvent("trial_purchase_completed", {
      paymentPath: "wallet",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
        status,
      },
    });
    await pushPurchaseEvent({
      clientReferenceId,
      paywall,
      value: purchaseValue,
      type: "trial",
    });
    await sendAmplitudePurchase({
      userId: clientReferenceId,
      revenue: purchaseValue,
      currency: paymentCurrencyCode,
      product: "Trial payment",
      paywall,
      type: "trial",
    });
  };

  const paymentIntentIdFrom = (data, fallback) =>
    String((data && (data.payment_intent_id || data.id)) || fallback || "").trim();

  const finishSuccessfulPayment = async (data, status = "succeeded", fallbackPaymentIntentId = "") => {
    setErrorMessage("");
    setLoading(false);
    await finishTrialPurchase(status);
    if (onSuccess) onSuccess(paymentIntentIdFrom(data, fallbackPaymentIntentId));
  };

  useEffect(() => {
    sendPaywallEvent("payment_options_seen", {
      paymentPath: "wallet",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
      },
    });
  }, [paymentCurrencyCode, computedPaymentAmount]);

  const options = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        theme: "dark",
        height: "55px",
      },
    },
  };

  useEffect(() => {
    if (!stripe || !trialCost) return;

    const amount = computedPaymentAmount;
    if (!amount || amount <= 0) return;

    const walletInteraction = createWalletInteractionAnalytics({ amount, currency: paymentCurrencyCode, wallet_surface: "standalone_button" });
    const pr = stripe.paymentRequest({
      country: "US",
      currency: paymentCurrencyCode,
      total: {
        label: walletPaymentLabel,
        amount,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    pr.on("cancel", () => walletInteraction.dismissed());
    walletInteractionRef.current.set(pr, walletInteraction);
    pr.canMakePayment()
      .then((res) => {
        walletInteraction.available(res);
        if (res) {
          sendPaywallEvent("wallet_available", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              ...getWalletAvailabilityPayload(res),
            },
          });
          dispatch(setApplePayAvailible(true));
          setPaymentRequest(pr);
          setPaymentRequestKey((prev) => prev + 1);
        } else {
          sendPaywallEvent("wallet_unavailable", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              payment_request_supported: false,
            },
          });
          dispatch(setApplePayAvailible(false));
        }
      })
      .catch(() => {
        sendPaywallEvent("wallet_unavailable", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            reason: "can_make_payment_error",
          },
        });
        dispatch(setApplePayAvailible(false));
      });

    pr.on("paymentmethod", async (e) => {
      // A wallet handler can outlive URL navigation. Freeze this attempt's attribution now.
      const clientReferenceId = getClientReferenceId();
      const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 46 });
      walletInteraction.authorized(e?.walletName);
      setLoading(true);
      setErrorMessage(""); // Clear previous errors
      const now = new Date();
      now.setDate(now.getDate() + TRIAL_DAYS);
      const trialEnd = Math.floor(now.valueOf() / 1000);

      // SUBSCRIPTION CREATION DISABLED - Use payment-only endpoint
      let existingCustomerId = null;
      try {
        existingCustomerId = sessionStorage.getItem("stripeCustomerId");
      } catch {}

      const bodyObj = {
        offerId,
        amount,
        id: e.paymentMethod.id,
        email: e.payerEmail || "applegoogle@email.com",
        data: {
          currency: paymentCurrencyCode,
          client_reference_id: clientReferenceId,
          price_id: metadataPriceId,
          ...utmMetadata,
          ...(acquisitionCountry ? { acquisition_country: acquisitionCountry } : {}),
          ...(paywall ? { paywall } : {}),
        },
        ...(existingCustomerId ? { customerId: existingCustomerId } : {}),
      };

      try {
        // Use payment endpoint instead of create-payment-intent to avoid subscriptions
        const idempotencyKey = `${e.paymentMethod.id}-${amount}`;
        const response = await axios.post(`${API_URL}/payment`, bodyObj, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        const returnedCustomerId =
          response?.data?.customerId || response?.data?.customer?.id;
        if (returnedCustomerId) {
          try {
            sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
          } catch {}
        }
        
        if (response.data.success) {
          try {
            sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
          } catch {}
          e.complete("success");
          await finishSuccessfulPayment(response.data);
        } else if (response.data.requires_action) {
          // Close wallet sheet before starting 3DS
          e.complete("success");
          sendPaywallEvent("wallet_payment_requires_action", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "confirm_card_payment",
              status: "requires_action",
            },
          });
          try {
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              response.data.client_secret
            );
            if (confirmError) {
              setLoading(false);
              setErrorMessage(confirmError.message);
              sendPaywallEvent("wallet_payment_failed", {
                paymentPath: "wallet",
                payload: {
                  currency: paymentCurrencyCode,
                  amount,
                  error_stage: "confirm_card_payment",
                },
              });
            } else {
              const status = paymentIntent && paymentIntent.status;
              if (
                status === "succeeded" ||
                status === "processing" ||
                status === "requires_capture"
              ) {
                try {
                  sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
                } catch {}
                await finishSuccessfulPayment(response.data, status, paymentIntent?.id);
              } else {
                setLoading(false);
                setErrorMessage(
                  "Payment authentication failed. Please try again."
                );
                sendPaywallEvent("wallet_payment_failed", {
                  paymentPath: "wallet",
                  payload: {
                    currency: paymentCurrencyCode,
                    amount,
                    error_stage: "confirm_card_payment",
                    status,
                  },
                });
              }
            }
          } catch (err) {
            setLoading(false);
            setErrorMessage("Payment authentication failed. Please try again.");
            sendPaywallEvent("wallet_payment_failed", {
              paymentPath: "wallet",
              payload: {
                currency: paymentCurrencyCode,
                amount,
                error_stage: "confirm_card_payment",
              },
            });
          }
        } else {
          e.complete("fail");
          setLoading(false);
          setErrorMessage(response.data.message || "Payment failed. Please try again.");
          sendPaywallEvent("wallet_payment_failed", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "payment_endpoint",
            },
          });
        }
      } catch (error) {
        console.log("Apple Pay error", error);
        e.complete("fail");
        setLoading(false);
        setErrorMessage("Payment failed. Please try again.");
        sendPaywallEvent("wallet_payment_failed", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            error_stage: "payment_endpoint",
          },
        });
      }
    });
  }, [stripe, acquisitionCountry, trialCost, computedPaymentAmount, paymentCurrencyCode, metadataPriceId, offerId, purchaseValue, walletPaymentLabel]);

  if (!paymentRequest) return null;

  return (
    <div
      className={`mb-4 ${isDisabled ? "opacity-50 pointer-events-none" : ""} ${
        loading ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div
        key={paymentRequestKey}
        style={{
          marginBottom: 8,
          overflow: "hidden",
          borderRadius: 8,
        }}
      >
        <PaymentRequestButtonElement
                onClick={() => walletInteractionRef.current.get(paymentRequest)?.clicked()}
          options={options}
          className="apple-google-pay"
        />
      </div>
      {errorMessage && (
        <div className="mt-2 text-red-600 text-sm text-center">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default AppleGooglePayButton;
